import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API_BASE } from '../config/api';
import { 
  BookOpen, 
  Award, 
  ArrowLeft, 
  FileText, 
  Clock, 
  CheckCircle2,
  X,
  Loader2,
  ExternalLink,
  Play,
  Download,
  Maximize2,
  Minimize2
} from 'lucide-react';
import { toast } from 'sonner';
import { Layout } from '../components/layout/Layout';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
const API = `${API_BASE}/api`;

export default function EBookDetailPage() {
  const { ebookId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  
  const [ebook, setEbook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  
  // Quiz state
  const [showQuiz, setShowQuiz] = useState(false);
  const [quiz, setQuiz] = useState(null);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [quizResults, setQuizResults] = useState(null);
  const [submittingQuiz, setSubmittingQuiz] = useState(false);

  useEffect(() => {
    fetchEbook();
  }, [ebookId, token]);

  const fetchEbook = async () => {
    try {
      const response = await axios.get(`${API}/ebooks/${ebookId}`);
      setEbook(response.data);
      
      // Fetch quiz if available (requires auth token)
      if (response.data.has_quiz && response.data.quiz_id && token) {
        try {
          const quizRes = await axios.get(`${API}/quizzes/${response.data.quiz_id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setQuiz(quizRes.data);
        } catch (err) {
          console.log('Quiz fetch failed:', err);
        }
      }
    } catch (error) {
      console.error('Failed to fetch eBook:', error);
      toast.error('Failed to load eBook');
    } finally {
      setLoading(false);
    }
  };

  const handleReadEbook = async () => {
    if (!ebook?.file_path) {
      toast.error('PDF file not available');
      return;
    }
    
    setLoadingPdf(true);
    try {
      const response = await axios.get(`${API}/ebooks/${ebookId}/file`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      // Use the proxy endpoint with token for authenticated access
      const proxyUrl = `${API}/files/${response.data.file_path}${token ? `?token=${token}` : ''}`;
      setPdfUrl(proxyUrl);
      setPdfViewerOpen(true);
    } catch (error) {
      console.error('Failed to get PDF:', error);
      toast.error('Failed to load PDF');
    } finally {
      setLoadingPdf(false);
    }
  };

  const openPdfInNewTab = () => {
    if (pdfUrl) {
      window.open(pdfUrl, '_blank');
    }
  };

  const handleQuizAnswer = (questionIndex, answer) => {
    setQuizAnswers(prev => ({ ...prev, [questionIndex]: answer }));
  };

  const handleSubmitQuiz = async () => {
    if (!quiz) return;
    
    setSubmittingQuiz(true);
    try {
      const response = await axios.post(`${API}/quizzes/${quiz.quiz_id}/submit`, {
        answers: quizAnswers
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setQuizResults(response.data);
      setQuizSubmitted(true);
      
      if (response.data.passed) {
        toast.success('Congratulations! You passed the quiz!');
      } else {
        toast.info('Quiz completed. Review your answers below.');
      }
    } catch (error) {
      console.error('Quiz submission failed:', error);
      toast.error('Failed to submit quiz');
    } finally {
      setSubmittingQuiz(false);
    }
  };

  const resetQuiz = () => {
    setQuizAnswers({});
    setQuizSubmitted(false);
    setQuizResults(null);
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="h-8 w-8 text-navigate-gold animate-spin" />
        </div>
      </Layout>
    );
  }

  if (!ebook) {
    return (
      <Layout>
        <div className="min-h-screen flex flex-col items-center justify-center">
          <BookOpen className="h-16 w-16 text-zinc-600 mb-4" />
          <h2 className="text-xl text-white mb-2">eBook not found</h2>
          <Link to="/ebooks" className="text-navigate-gold hover:underline">
            Back to eBooks
          </Link>
        </div>
      </Layout>
    );
  }

  const defaultThumbnail = "https://images.unsplash.com/photo-1772588627474-ae6acc69ac42?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwyfHxjb21wbGlhbmNlJTIwZG9jdW1lbnR8ZW58MHx8fHwxNzc0NDQ4NjUzfDA&ixlib=rb-4.1.0&q=85";

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <Link 
          to="/ebooks" 
          className="inline-flex items-center gap-2 text-zinc-400 hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to eBooks
        </Link>

        {/* Main Content */}
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Cover Image */}
          <div className="lg:col-span-1">
            <div className="aspect-[3/4] rounded-xl overflow-hidden bg-zinc-800 relative">
              <img 
                src={ebook.thumbnail_url || defaultThumbnail}
                alt={ebook.title}
                className="w-full h-full object-cover"
                onError={(e) => { e.target.src = defaultThumbnail; }}
              />
              
              {/* Badges */}
              <div className="absolute top-4 left-4 flex flex-col gap-2">
                {ebook.cpd_points > 0 && (
                  <div className="bg-navigate-gold text-black px-3 py-1.5 rounded-lg text-sm font-bold flex items-center gap-1">
                    <Award className="h-4 w-4" />
                    {ebook.cpd_points} CPD Points
                  </div>
                )}
                {ebook.has_quiz && (
                  <div className="bg-green-500 text-white px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1">
                    <CheckCircle2 className="h-4 w-4" />
                    Quiz Available
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Details */}
          <div className="lg:col-span-2">
            {/* Category */}
            <span className="text-navigate-gold text-sm font-medium uppercase tracking-wider">
              {ebook.category}
            </span>

            {/* Title */}
            <h1 className="font-heading text-3xl lg:text-4xl font-bold text-white mt-2 mb-4">
              {ebook.title}
            </h1>

            {/* Description */}
            <p className="text-zinc-300 text-lg leading-relaxed mb-6">
              {ebook.description}
            </p>

            {/* Info */}
            <div className="flex flex-wrap items-center gap-4 mb-8 text-sm text-zinc-400">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                PDF Document
              </div>
              {ebook.cpd_points > 0 && (
                <div className="flex items-center gap-2">
                  <Award className="h-4 w-4" />
                  {ebook.cpd_points} CPD Points upon completion
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-4">
              <Button
                onClick={handleReadEbook}
                disabled={loadingPdf || !ebook.file_path}
                className="bg-navigate-gold hover:bg-navigate-gold/90 text-black font-medium px-6 py-3 h-auto"
                data-testid="read-ebook-btn"
              >
                {loadingPdf ? (
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                ) : (
                  <BookOpen className="h-5 w-5 mr-2" />
                )}
                {loadingPdf ? 'Loading...' : 'Read eBook'}
              </Button>

              {ebook.has_quiz && quiz && (
                <Button
                  onClick={() => setShowQuiz(true)}
                  variant="outline"
                  className="border-green-500 text-green-400 hover:bg-green-500/10 px-6 py-3 h-auto"
                  data-testid="take-quiz-btn"
                >
                  <Play className="h-5 w-5 mr-2" />
                  Take Quiz
                </Button>
              )}

              {!ebook.file_path && (
                <p className="text-zinc-500 text-sm">PDF file not yet uploaded</p>
              )}
            </div>

            {/* Quiz Preview */}
            {ebook.has_quiz && quiz && (
              <div className="mt-8 p-6 bg-zinc-800/50 rounded-xl border border-zinc-700">
                <h3 className="text-white font-semibold mb-2 flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-400" />
                  Quiz Available
                </h3>
                <p className="text-zinc-400 text-sm mb-3">
                  Test your knowledge with {quiz.questions?.length || 0} questions. 
                  Pass mark: {quiz.pass_mark || 70}%
                </p>
                <Button
                  onClick={() => setShowQuiz(true)}
                  size="sm"
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  Start Quiz
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* PDF Viewer Modal */}
      {pdfViewerOpen && pdfUrl && (
        <div className={`fixed inset-0 z-50 bg-black flex flex-col ${fullscreen ? '' : ''}`}>
          {/* Header */}
          <div className="bg-zinc-900 border-b border-zinc-700 px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <BookOpen className="h-5 w-5 text-navigate-gold" />
              <h3 className="text-white font-medium truncate max-w-md">{ebook.title}</h3>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={openPdfInNewTab}
                className="text-zinc-400 hover:text-white"
              >
                <ExternalLink className="h-4 w-4 mr-1" />
                Open in New Tab
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setFullscreen(!fullscreen)}
                className="text-zinc-400 hover:text-white"
              >
                {fullscreen ? (
                  <Minimize2 className="h-4 w-4" />
                ) : (
                  <Maximize2 className="h-4 w-4" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setPdfViewerOpen(false)}
                className="text-zinc-400 hover:text-white"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
          </div>

          {/* PDF Content */}
          <div className="flex-1 bg-zinc-800 relative">
            {/* Use object tag with iframe fallback for better PDF support */}
            <object
              data={pdfUrl}
              type="application/pdf"
              className="w-full h-full"
              title={ebook.title}
              data-testid="pdf-viewer"
            >
              {/* Fallback iframe */}
              <iframe
                src={pdfUrl}
                className="w-full h-full border-0"
                title={ebook.title}
              />
            </object>
            
            {/* Fallback message for browsers that can't render PDF inline */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-zinc-900/95 border border-zinc-700 rounded-lg px-4 py-2 flex items-center gap-3">
              <p className="text-zinc-400 text-sm">
                PDF not displaying?
              </p>
              <Button
                size="sm"
                onClick={openPdfInNewTab}
                className="bg-navigate-gold hover:bg-navigate-gold/90 text-black text-xs"
              >
                <ExternalLink className="h-3 w-3 mr-1" />
                Open in New Tab
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Quiz Modal */}
      {showQuiz && quiz && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-navigate-surface border border-zinc-800 rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            {/* Quiz Header */}
            <div className="sticky top-0 bg-navigate-surface border-b border-zinc-800 px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-white">{quiz.title || 'Quiz'}</h2>
                <p className="text-sm text-zinc-400">
                  {quiz.questions?.length || 0} questions • Pass mark: {quiz.pass_mark || 70}%
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowQuiz(false);
                  if (!quizSubmitted) resetQuiz();
                }}
                className="text-zinc-400 hover:text-white"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>

            {/* Quiz Content */}
            <div className="p-6">
              {!quizSubmitted ? (
                // Quiz Questions
                <div className="space-y-8">
                  {quiz.questions?.map((q, qIndex) => (
                    <div key={q.question_id || qIndex} className="bg-zinc-800/50 rounded-xl p-6">
                      <div className="flex items-start gap-4 mb-4">
                        <span className="w-8 h-8 bg-navigate-gold text-black rounded-full flex items-center justify-center font-bold text-sm shrink-0">
                          {qIndex + 1}
                        </span>
                        <div>
                          {q.question_type === 'true_false' && (
                            <span className="text-xs text-blue-400 bg-blue-400/20 px-2 py-0.5 rounded mb-2 inline-block">
                              True/False
                            </span>
                          )}
                          <p className="text-white font-medium">{q.question}</p>
                        </div>
                      </div>

                      <div className="space-y-2 ml-12">
                        {q.options?.map((option, optIndex) => (
                          <button
                            key={optIndex}
                            onClick={() => handleQuizAnswer(qIndex, option)}
                            className={`w-full p-4 rounded-lg text-left transition-all border ${
                              quizAnswers[qIndex] === option
                                ? 'bg-navigate-gold/20 border-navigate-gold text-white'
                                : 'bg-zinc-900/50 border-zinc-700 text-zinc-300 hover:border-zinc-600'
                            }`}
                          >
                            {option}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}

                  {/* Submit Button */}
                  <div className="flex justify-center pt-4">
                    <Button
                      onClick={handleSubmitQuiz}
                      disabled={submittingQuiz || Object.keys(quizAnswers).length < quiz.questions?.length}
                      className="bg-navigate-gold hover:bg-navigate-gold/90 text-black font-medium px-8 py-3"
                    >
                      {submittingQuiz ? (
                        <Loader2 className="h-5 w-5 animate-spin mr-2" />
                      ) : (
                        <CheckCircle2 className="h-5 w-5 mr-2" />
                      )}
                      {submittingQuiz ? 'Submitting...' : 'Submit Quiz'}
                    </Button>
                  </div>

                  {Object.keys(quizAnswers).length < quiz.questions?.length && (
                    <p className="text-center text-zinc-500 text-sm">
                      Please answer all {quiz.questions?.length} questions to submit
                    </p>
                  )}
                </div>
              ) : (
                // Quiz Results
                <div>
                  {/* Score Card */}
                  <div className={`text-center p-8 rounded-xl mb-6 ${
                    quizResults?.passed 
                      ? 'bg-green-500/10 border border-green-500/30' 
                      : 'bg-red-500/10 border border-red-500/30'
                  }`}>
                    <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 ${
                      quizResults?.passed ? 'bg-green-500/20' : 'bg-red-500/20'
                    }`}>
                      {quizResults?.passed ? (
                        <CheckCircle2 className="h-10 w-10 text-green-400" />
                      ) : (
                        <X className="h-10 w-10 text-red-400" />
                      )}
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-2">
                      {quizResults?.passed ? 'Congratulations!' : 'Not Quite'}
                    </h3>
                    <p className="text-4xl font-bold mb-2" style={{ color: quizResults?.passed ? '#4ade80' : '#f87171' }}>
                      {Math.round(quizResults?.score || 0)}%
                    </p>
                    <p className="text-zinc-400">
                      {quizResults?.passed 
                        ? 'You have passed this quiz!' 
                        : `You need ${quiz.pass_mark || 70}% to pass`}
                    </p>
                  </div>

                  {/* Review Answers */}
                  <h4 className="text-lg font-semibold text-white mb-4">Review Your Answers</h4>
                  <div className="space-y-4">
                    {quizResults?.results?.map((result, idx) => (
                      <div 
                        key={idx}
                        className={`p-4 rounded-lg border ${
                          result.is_correct 
                            ? 'bg-green-500/10 border-green-500/30' 
                            : 'bg-red-500/10 border-red-500/30'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          {result.is_correct ? (
                            <CheckCircle2 className="h-5 w-5 text-green-400 shrink-0 mt-0.5" />
                          ) : (
                            <X className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
                          )}
                          <div className="flex-1">
                            <p className="text-white font-medium mb-1">
                              {quiz.questions?.[idx]?.question}
                            </p>
                            <p className={`text-sm ${result.is_correct ? 'text-green-400' : 'text-red-400'}`}>
                              Your answer: {result.user_answer}
                            </p>
                            {!result.is_correct && (
                              <p className="text-sm text-green-400 mt-1">
                                Correct answer: {result.correct_answer}
                              </p>
                            )}
                            {result.explanation && (
                              <p className="text-sm text-zinc-400 mt-2 italic">
                                {result.explanation}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Actions */}
                  <div className="flex justify-center gap-4 mt-6">
                    {!quizResults?.passed && (
                      <Button
                        onClick={resetQuiz}
                        variant="outline"
                        className="border-zinc-600"
                      >
                        Try Again
                      </Button>
                    )}
                    <Button
                      onClick={() => setShowQuiz(false)}
                      className="bg-navigate-gold hover:bg-navigate-gold/90 text-black"
                    >
                      Close
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
